import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Target,
  Search,
  Package,
  BookOpen,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Play,
  RotateCcw,
  Lightbulb,
  FlaskConical,
  Database,
  ShoppingCart,
  TrendingUp,
  Copy,
  Check,
  MessageSquare,
  Quote,
  ExternalLink,
  Star
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

interface Step {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'running' | 'completed' | 'error';
  data?: any;
  duration?: number;
}

interface TestState {
  question: {
    id: string;
    question_text: string;
    answer_type: string;
  };
  wheelId: string;
  siteId: string;
  category: string;
}

interface Recommendation {
  type: string;
  priority: string;
  title: string;
  description: string;
  action: string;
  actionLabel: string;
  data: any;
}

interface Diagnostics {
  answerQualityScore: number;
  searchVolume: number;
  gaps: {
    benefitMapping: boolean;
    scientificContext: boolean;
    attributeEnrichment: boolean;
    productSourcing: boolean;
  };
  details: {
    benefitMapping: any;
    scientificContext: any;
    attributeEnrichment: any;
    productSourcing: any;
  };
  recommendations: Recommendation[];
  summary: {
    productsFound: number;
    productsWithMatchingAttrs: number;
    attrCoveragePercent: number;
    highScoreProducts: number;
  };
}

export default function AIAnswerTest() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as TestState;

  const [steps, setSteps] = useState<Step[]>([
    {
      id: 'benefits',
      title: 'Extract Implied Benefits',
      description: 'Identify what customer benefits are implied by this question',
      icon: <Target className="w-5 h-5" />,
      status: 'pending'
    },
    {
      id: 'attributes',
      title: 'Determine Technical Attributes',
      description: 'Map benefits to measurable product attributes',
      icon: <Brain className="w-5 h-5" />,
      status: 'pending'
    },
    {
      id: 'query',
      title: 'Formulate Product Query',
      description: 'Create search strategy to find matching products',
      icon: <Search className="w-5 h-5" />,
      status: 'pending'
    },
    {
      id: 'products',
      title: 'Find & Rank Products',
      description: 'Execute queries and rank products by relevance',
      icon: <Package className="w-5 h-5" />,
      status: 'pending'
    },
    {
      id: 'context',
      title: 'Prepare Scientific Context',
      description: 'Gather research about attribute-benefit relationships',
      icon: <BookOpen className="w-5 h-5" />,
      status: 'pending'
    },
    {
      id: 'answer',
      title: 'Generate Answer',
      description: 'Create comprehensive answer with product recommendations',
      icon: <Sparkles className="w-5 h-5" />,
      status: 'pending'
    }
  ]);

  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [isRunning, setIsRunning] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [finalAnswer, setFinalAnswer] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<Diagnostics | null>(null);
  const [triggeringAction, setTriggeringAction] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Helper to get products from steps
  const getProductsFromSteps = () => {
    const productsStep = steps.find(s => s.id === 'products');
    return productsStep?.data?.products || [];
  };

  // Helper to get research items from steps
  const getResearchFromSteps = () => {
    const contextStep = steps.find(s => s.id === 'context');
    return contextStep?.data?.researchItems || [];
  };

  // Copy answer to clipboard
  const copyAnswer = async (answerHtml: string) => {
    // Strip HTML tags for plain text copy
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = answerHtml;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';

    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!state?.question) {
    return (
      <div className="container mx-auto py-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No question selected. Please go back and select a question to test.
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const toggleStepExpanded = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const updateStep = (stepId: string, updates: Partial<Step>) => {
    setSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, ...updates } : step
    ));
  };

  const runTest = async () => {
    setIsRunning(true);
    setError(null);
    setFinalAnswer(null);
    setDiagnostics(null);

    // Reset all steps
    setSteps(prev => prev.map(step => ({ ...step, status: 'pending', data: undefined, duration: undefined })));
    setExpandedSteps(new Set());

    try {
      // Call the backend endpoint that runs the full test
      const response = await authFetch(`${API_URL}/answer-generation/test-pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionId: state.question.id,
          questionText: state.question.question_text,
          wheelId: state.wheelId,
          siteId: state.siteId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to run AI answer test');
      }

      const result = await response.json();

      // Update each step with results
      if (result.steps) {
        for (const stepResult of result.steps) {
          updateStep(stepResult.id, {
            status: stepResult.error ? 'error' : 'completed',
            data: stepResult.data,
            duration: stepResult.duration
          });

          // Auto-expand completed steps with data
          if (stepResult.data) {
            setExpandedSteps(prev => new Set([...prev, stepResult.id]));
          }
        }
      }

      if (result.finalAnswer) {
        setFinalAnswer(result.finalAnswer);
      }

      // Capture diagnostics
      if (result.diagnostics) {
        setDiagnostics(result.diagnostics);
      }

    } catch (err) {
      console.error('AI Answer Test error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsRunning(false);
    }
  };

  const handleTriggerAction = async (recommendation: Recommendation) => {
    setTriggeringAction(recommendation.action);
    try {
      switch (recommendation.action) {
        case 'trigger_benefit_research':
          // Navigate to topics page for benefit-attribute research
          navigate('/admin/topics', {
            state: {
              researchType: 'benefit_attribute_mapping',
              suggestedTopics: recommendation.data.benefits?.map((b: string) =>
                `How does ${b} relate to measurable product attributes in ${recommendation.data.category}?`
              ),
              category: recommendation.data.category
            }
          });
          break;
        case 'trigger_context_research':
          // Navigate to topics page for scientific context research
          navigate('/admin/topics', {
            state: {
              researchType: 'scientific_context',
              suggestedTopics: recommendation.data.topics,
              category: recommendation.data.category
            }
          });
          break;
        case 'trigger_attribute_enrichment':
          // Navigate to product management or show detailed modal
          navigate('/products', {
            state: {
              action: 'enrich_attributes',
              attributes: recommendation.data.attributes,
              category: recommendation.data.category,
              productsToEnrich: recommendation.data.productsToEnrich
            }
          });
          break;
        case 'view_sourcing_recommendations':
          // Navigate to product sourcing view
          navigate('/products', {
            state: {
              action: 'source_products',
              attributesNeeded: recommendation.data.attributesNeeded,
              category: recommendation.data.category,
              currentOptions: recommendation.data.currentOptions
            }
          });
          break;
        default:
          console.log('Unknown action:', recommendation.action);
      }
    } finally {
      setTriggeringAction(null);
    }
  };

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'research': return <FlaskConical className="w-5 h-5" />;
      case 'enrichment': return <Database className="w-5 h-5" />;
      case 'sourcing': return <ShoppingCart className="w-5 h-5" />;
      default: return <Lightbulb className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStepStatusIcon = (status: Step['status']) => {
    switch (status) {
      case 'pending':
        return <div className="w-6 h-6 rounded-full border-2 border-gray-300" />;
      case 'running':
        return <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="w-6 h-6 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
    }
  };

  const getStepStatusColor = (status: Step['status']) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
      case 'running': return 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800';
      case 'completed': return 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800';
      case 'error': return 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';
    }
  };

  const renderStepData = (step: Step) => {
    if (!step.data) return null;

    switch (step.id) {
      case 'benefits':
        return (
          <div className="space-y-3">
            {/* Show known benefits context */}
            {step.data.knownBenefitsCount > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                  Constrained to {step.data.knownBenefitsCount} known benefits in knowledge base
                </div>
                <div className="flex flex-wrap gap-1">
                  {step.data.knownBenefits?.map((b: string, i: number) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded">
                      {b}
                    </span>
                  ))}
                  {step.data.knownBenefitsCount > 10 && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">+{step.data.knownBenefitsCount - 10} more</span>
                  )}
                </div>
              </div>
            )}

            <h4 className="font-medium text-sm">Mapped Benefits:</h4>
            <div className="flex flex-wrap gap-2">
              {step.data.benefits?.map((benefit: any, i: number) => (
                <Badge key={i} variant="secondary" className="bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200">
                  {benefit.name}
                  {benefit.confidence && (
                    <span className="ml-1 text-xs opacity-70">({Math.round(benefit.confidence * 100)}%)</span>
                  )}
                </Badge>
              ))}
            </div>
            {step.data.reasoning && (
              <p className="text-sm text-muted-foreground mt-2">{step.data.reasoning}</p>
            )}
          </div>
        );

      case 'attributes':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Technical Attributes:</h4>
            <div className="space-y-2">
              {step.data.attributes?.map((attr: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">{attr.name}</Badge>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{attr.benefit}</span>
                  {attr.impact && (
                    <Badge variant={attr.impact === 'high' ? 'default' : 'secondary'} className="ml-auto">
                      {attr.impact} impact
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'query':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Search Strategy:</h4>
            <div className="bg-gray-900 text-gray-100 p-3 rounded-md text-sm font-mono overflow-x-auto">
              {step.data.queries?.map((query: any, i: number) => (
                <div key={i} className="mb-2">
                  <span className="text-blue-400">Query {i + 1}:</span> {query.terms?.join(', ')}
                  {query.filters && (
                    <div className="ml-4 text-gray-400">
                      Filters: {JSON.stringify(query.filters)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'products':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-sm">
              Found {step.data.products?.length || 0} Matching Products:
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Rank</th>
                    <th className="text-left py-2 px-2">Product</th>
                    <th className="text-left py-2 px-2">Score</th>
                    <th className="text-left py-2 px-2">Key Attributes</th>
                  </tr>
                </thead>
                <tbody>
                  {step.data.products?.slice(0, 10).map((product: any, i: number) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 px-2">
                        <Badge variant={i < 3 ? 'default' : 'secondary'}>{i + 1}</Badge>
                      </td>
                      <td className="py-2 px-2">
                        <div className="font-medium">{product.title?.substring(0, 50)}...</div>
                        <div className="text-xs text-muted-foreground">{product.brand}</div>
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500"
                              style={{ width: `${product.score || 0}%` }}
                            />
                          </div>
                          <span className="text-xs">{product.score || 0}%</span>
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex flex-wrap gap-1">
                          {product.matchingAttributes?.slice(0, 3).map((attr: string, j: number) => (
                            <Badge key={j} variant="outline" className="text-xs">
                              {attr}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'context':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Scientific Context:</h4>
            <div className="space-y-2">
              {step.data.researchItems?.map((item: any, i: number) => (
                <div key={i} className="p-3 bg-gray-50 rounded-md">
                  <div className="font-medium text-sm">{item.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{item.summary}</p>
                  {item.source && (
                    <div className="text-xs text-blue-600 mt-1">{item.source}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'answer':
        const topProducts = getProductsFromSteps().slice(0, 5);
        const researchItems = getResearchFromSteps();

        return (
          <div className="space-y-4">
            {/* Answer Header with Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-lg">Generated Answer</h4>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyAnswer(step.data.answer || '')}
                className="gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Answer
                  </>
                )}
              </Button>
            </div>

            {/* Main Answer Card */}
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg opacity-20"></div>
              <div className="relative bg-white dark:bg-gray-800 border-2 border-green-200 dark:border-green-800 rounded-lg p-6 shadow-sm">
                <Quote className="absolute top-4 left-4 w-8 h-8 text-green-100 dark:text-green-900" />
                <div
                  className="prose prose-sm dark:prose-invert max-w-none pl-6
                    prose-headings:text-gray-800 dark:prose-headings:text-gray-100 prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
                    prose-h3:text-base prose-h3:border-b prose-h3:border-gray-200 dark:prose-h3:border-gray-600 prose-h3:pb-1
                    prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:my-2
                    prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-strong:font-semibold
                    prose-ul:my-2 prose-li:my-0.5 prose-li:text-gray-700 dark:prose-li:text-gray-300
                    prose-table:text-sm prose-table:my-4 prose-table:border-collapse
                    [&_table]:w-full [&_table]:border [&_table]:border-gray-200 dark:[&_table]:border-gray-600
                    [&_th]:bg-gray-100 dark:[&_th]:bg-gray-700 [&_th]:p-2 [&_th]:text-left [&_th]:font-medium [&_th]:border [&_th]:border-gray-200 dark:[&_th]:border-gray-600 dark:[&_th]:text-gray-200
                    [&_td]:p-2 [&_td]:border [&_td]:border-gray-200 dark:[&_td]:border-gray-600 dark:[&_td]:text-gray-300
                    [&_.product-rec]:bg-blue-50 dark:[&_.product-rec]:bg-blue-900/30 [&_.product-rec]:p-3 [&_.product-rec]:rounded-md [&_.product-rec]:my-2 [&_.product-rec]:border-l-4 [&_.product-rec]:border-blue-400"
                  dangerouslySetInnerHTML={{ __html: step.data.answer || '' }}
                />
              </div>
            </div>

            {/* Sources Section */}
            {(topProducts.length > 0 || researchItems.length > 0) && (
              <div className="mt-6 space-y-4">
                <Separator />

                {/* Product Sources */}
                {topProducts.length > 0 && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-sm flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-600" />
                      Products Referenced ({topProducts.length})
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {topProducts.map((product: any, i: number) => (
                        <div
                          key={i}
                          className="p-3 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate" title={product.title}>
                                {product.title?.substring(0, 40)}{product.title?.length > 40 ? '...' : ''}
                              </div>
                              <div className="text-xs text-muted-foreground">{product.brand}</div>
                            </div>
                            <Badge
                              variant={product.score >= 80 ? 'default' : product.score >= 60 ? 'secondary' : 'outline'}
                              className="shrink-0"
                            >
                              {product.score}%
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm font-semibold text-green-700">
                              {product.price} {product.currency}
                            </span>
                            {product.score >= 80 && (
                              <div className="flex items-center gap-1 text-xs text-yellow-600">
                                <Star className="w-3 h-3 fill-yellow-500" />
                                Top Match
                              </div>
                            )}
                          </div>
                          {product.matchingAttributes?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {product.matchingAttributes.slice(0, 2).map((attr: string, j: number) => (
                                <Badge key={j} variant="outline" className="text-xs bg-white">
                                  {attr.split(':')[0]}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Research Sources */}
                {researchItems.length > 0 && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-sm flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-purple-600" />
                      Research Context ({researchItems.length})
                    </h5>
                    <div className="space-y-2">
                      {researchItems.map((item: any, i: number) => (
                        <div
                          key={i}
                          className="p-3 bg-gradient-to-br from-purple-50 to-gray-50 dark:from-purple-900/30 dark:to-gray-800 rounded-lg border border-purple-100 dark:border-purple-800"
                        >
                          <div className="font-medium text-sm text-purple-900 dark:text-purple-200">{item.title}</div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{item.summary}</p>
                          {item.source && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-purple-600">
                              <ExternalLink className="w-3 h-3" />
                              {item.source}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                <div className="flex items-center justify-center gap-6 pt-3 text-sm text-muted-foreground border-t">
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    {step.data.sources?.length || topProducts.length} products analyzed
                  </div>
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    {step.data.researchSources || researchItems.length} research sources
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
            {JSON.stringify(step.data, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button onClick={() => navigate(-1)} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          <h1 className="text-3xl font-bold">AI Answer Test</h1>
          <p className="text-muted-foreground">
            Step-by-step analysis of how AI answers questions using your catalog
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runTest}
            disabled={isRunning}
            size="lg"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Test...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Test
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Question Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Test Question</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-lg font-medium">{state.question.question_text}</p>
          <div className="flex gap-2 mt-3">
            <Badge variant="secondary">{state.question.answer_type}</Badge>
            <Badge variant="outline">Wheel: {state.wheelId?.substring(0, 8)}...</Badge>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => (
          <Card
            key={step.id}
            className={`transition-all ${getStepStatusColor(step.status)}`}
          >
            <CardHeader
              className="cursor-pointer"
              onClick={() => step.data && toggleStepExpanded(step.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-700 border dark:border-gray-600">
                    {step.icon}
                  </div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      Step {index + 1}: {step.title}
                      {step.duration && (
                        <span className="text-xs text-muted-foreground font-normal">
                          ({step.duration}ms)
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>{step.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStepStatusIcon(step.status)}
                  {step.data && (
                    expandedSteps.has(step.id) ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )
                  )}
                </div>
              </div>
            </CardHeader>
            {expandedSteps.has(step.id) && step.data && (
              <CardContent className="pt-0">
                <Separator className="mb-4" />
                {renderStepData(step)}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Final Answer Summary */}
      {finalAnswer && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Final Answer Generated
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm dark:prose-invert max-w-none bg-white dark:bg-gray-800 p-4 rounded-md border dark:border-gray-700">
              <div dangerouslySetInnerHTML={{ __html: finalAnswer }} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Diagnostics & Recommendations Section */}
      {diagnostics && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                Answer Quality Diagnostics
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-700">{diagnostics.answerQualityScore}%</div>
                  <div className="text-xs text-orange-600">Quality Score</div>
                </div>
                {diagnostics.searchVolume > 0 && (
                  <div className="text-right border-l pl-3">
                    <div className="text-lg font-semibold text-gray-700">{diagnostics.searchVolume.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Monthly Searches</div>
                  </div>
                )}
              </div>
            </div>
            <CardDescription>
              Identified gaps and recommended actions to improve answer quality
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border dark:border-gray-700">
                <div className="text-2xl font-bold">{diagnostics.summary.productsFound}</div>
                <div className="text-sm text-muted-foreground">Products Found</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border dark:border-gray-700">
                <div className="text-2xl font-bold">{diagnostics.summary.productsWithMatchingAttrs}</div>
                <div className="text-sm text-muted-foreground">With Matching Attrs</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border dark:border-gray-700">
                <div className="text-2xl font-bold">{diagnostics.summary.attrCoveragePercent}%</div>
                <div className="text-sm text-muted-foreground">Attr Coverage</div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border dark:border-gray-700">
                <div className="text-2xl font-bold">{diagnostics.summary.highScoreProducts}</div>
                <div className="text-sm text-muted-foreground">High Score Products</div>
              </div>
            </div>

            {/* Gap Indicators */}
            <div className="flex flex-wrap gap-2">
              {diagnostics.gaps.benefitMapping && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="w-3 h-3" /> Missing Benefit Mappings
                </Badge>
              )}
              {diagnostics.gaps.scientificContext && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="w-3 h-3" /> Missing Scientific Context
                </Badge>
              )}
              {diagnostics.gaps.attributeEnrichment && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="w-3 h-3" /> Products Need Enrichment
                </Badge>
              )}
              {diagnostics.gaps.productSourcing && (
                <Badge className="gap-1 bg-yellow-500">
                  <AlertCircle className="w-3 h-3" /> Limited Product Options
                </Badge>
              )}
              {!diagnostics.gaps.benefitMapping && !diagnostics.gaps.scientificContext &&
               !diagnostics.gaps.attributeEnrichment && !diagnostics.gaps.productSourcing && (
                <Badge variant="default" className="gap-1 bg-green-500">
                  <CheckCircle2 className="w-3 h-3" /> All Systems Ready
                </Badge>
              )}
            </div>

            {/* Recommendations with Action Buttons */}
            {diagnostics.recommendations.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  What We Need To Improve
                </h4>
                {diagnostics.recommendations.map((rec, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${getPriorityColor(rec.priority)}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-0.5">{getRecommendationIcon(rec.type)}</div>
                        <div className="flex-1">
                          <div className="font-semibold">{rec.title}</div>
                          <div className="text-sm opacity-80 mt-1">{rec.description}</div>

                          {/* The Ask - clear action statement */}
                          {rec.data?.ask && (
                            <div className="mt-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded border-l-4 border-orange-400">
                              <div className="text-xs font-medium text-orange-700 dark:text-orange-400 mb-1">ACTION NEEDED:</div>
                              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{rec.data.ask}</div>
                            </div>
                          )}

                          {/* Show specific items */}
                          {rec.data && (
                            <div className="mt-3 space-y-2">
                              {/* Benefits for research */}
                              {rec.type === 'research' && rec.data.benefits && (
                                <div>
                                  <div className="text-xs text-gray-600 mb-1">Benefits to research:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {rec.data.benefits.map((b: string, j: number) => (
                                      <Badge key={j} variant="secondary" className="text-xs bg-purple-100 text-purple-800">{b}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Attributes for enrichment */}
                              {rec.type === 'enrichment' && rec.data.attributes && (
                                <div>
                                  <div className="text-xs text-gray-600 mb-1">Missing attributes ({rec.data.productsToEnrich} products need this data):</div>
                                  <div className="flex flex-wrap gap-1">
                                    {rec.data.attributes.map((a: string, j: number) => (
                                      <Badge key={j} variant="secondary" className="text-xs bg-blue-100 text-blue-800">{a}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Attributes for sourcing */}
                              {rec.type === 'sourcing' && rec.data.attrDescriptions && rec.data.attrDescriptions.length > 0 && (
                                <div>
                                  <div className="text-xs text-gray-600 mb-1">Products needed with:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {rec.data.attrDescriptions.map((a: string, j: number) => (
                                      <Badge key={j} variant="secondary" className="text-xs bg-green-100 text-green-800">{a}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={rec.priority === 'high' ? 'destructive' : 'secondary'}
                        onClick={() => handleTriggerAction(rec)}
                        disabled={triggeringAction === rec.action}
                        className="shrink-0"
                      >
                        {triggeringAction === rec.action ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          rec.actionLabel
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
